import HttpClient from "api/index";
import {
	TExtendedSearchAbortResponse,
	TExtendedSearchFoundUrlsQuery,
	TExtendedSearchFoundUrlsResponse,
	TExtendedSearchImportProject,
	TExtendedSearchImportRequest,
	TExtendedSearchImportResponse,
	TExtendedSearchLimits,
	TExtendedSearchProjectCompaniesResponse,
	TExtendedSearchRunCreate,
	TExtendedSearchRunCreated,
	TExtendedSearchRunDetail,
	TExtendedSearchRunsResponse,
} from "api/extendedSearchApi/types";
import type {TExtendedSearchPipelineStatus} from "api/extendedSearchApi/pipelineTypes";
import type {
	TExtendedSearchImportQueue,
	TExtendedSearchQueueOrderRequest,
	TExtendedSearchQueuePauseRequest,
	TExtendedSearchQueueRunRequest,
} from "api/extendedSearchApi/queueTypes";
import {AxiosResponse} from "axios";

/**
 * Extended Search lives on the main API domain, so — unlike polarisApi — it takes no
 * baseURLOverride and needs no CSP entry in server.js.
 *
 * Every method resolves with the response even when the call failed: HttpClient turns a rejected
 * request into its error response (or `undefined` when the request never reached the server).
 * Callers must check `status` — see the thunks in store/extended-search.
 */
class ExtendedSearchApi extends HttpClient {
	async createRun(
		body: TExtendedSearchRunCreate,
		requestConfig = {},
	): Promise<AxiosResponse<TExtendedSearchRunCreated>> {
		return this.doPost<TExtendedSearchRunCreated, TExtendedSearchRunCreate>(
			"/extended-search/runs",
			body,
			requestConfig,
		);
	}

	async listRuns(requestConfig = {}): Promise<AxiosResponse<TExtendedSearchRunsResponse>> {
		return this.doGet<TExtendedSearchRunsResponse>("/extended-search/runs", requestConfig);
	}

	async getRun(
		extendedSearchRunId: number,
		requestConfig = {},
	): Promise<AxiosResponse<TExtendedSearchRunDetail>> {
		return this.doGet<TExtendedSearchRunDetail>(
			`/extended-search/runs/${extendedSearchRunId}`,
			requestConfig,
		);
	}

	/**
	 * Paginated: a run may hold thousands of URLs. `per_page` is never sent — the server applies
	 * its own page size and reports it back, so the page size stays a server-owned number.
	 */
	async getFoundUrls(
		extendedSearchRunId: number,
		query: TExtendedSearchFoundUrlsQuery,
		requestConfig = {},
	): Promise<AxiosResponse<TExtendedSearchFoundUrlsResponse>> {
		const status = query.status ? `&status=${encodeURIComponent(query.status)}` : "";

		return this.doGet<TExtendedSearchFoundUrlsResponse>(
			`/extended-search/runs/${extendedSearchRunId}/found-urls?page=${query.page}${status}`,
			requestConfig,
		);
	}

	/**
	 * The body states where the people go, and is always sent.
	 *
	 * `{project_id: null}` and no body at all are different requests: the first says "import these
	 * people onto no list", the second says nothing about the project and leaves whatever the run
	 * carries. The page always has an answer, so it always sends one — an omitted body here would
	 * make "no project" indistinguishable from "I forgot to ask".
	 */
	async startImport(
		extendedSearchRunId: number,
		body: TExtendedSearchImportRequest,
		requestConfig = {},
	): Promise<AxiosResponse<TExtendedSearchImportResponse>> {
		return this.doPost<TExtendedSearchImportResponse, TExtendedSearchImportRequest>(
			`/extended-search/runs/${extendedSearchRunId}/import`,
			body,
			requestConfig,
		);
	}

	/**
	 * Resolves a typed project id to its name, so the operator can see which project they picked
	 * before the import spends anything. 3365 and 3356 look equally plausible; the name does not.
	 */
	async getImportProject(
		projectId: number,
		requestConfig = {},
	): Promise<AxiosResponse<TExtendedSearchImportProject>> {
		return this.doGet<TExtendedSearchImportProject>(
			`/extended-search/projects/${projectId}`,
			requestConfig,
		);
	}

	/**
	 * Aborts discovery only. A run that has reached `Importing` is refused with a 409, because the
	 * per-profile money is committed by then and the shared import queue is not ours to purge —
	 * the thunk carries that refusal through so the operator reads the server's own reason.
	 */
	async abortRun(
		extendedSearchRunId: number,
		requestConfig = {},
	): Promise<AxiosResponse<TExtendedSearchAbortResponse>> {
		return this.doPost<TExtendedSearchAbortResponse, Record<string, never>>(
			`/extended-search/runs/${extendedSearchRunId}/abort`,
			undefined,
			requestConfig,
		);
	}

	/**
	 * Takes a run with nothing in flight out of the working list and the import queue, and answers
	 * with the run as it now stands. Created, Running and Importing runs are refused with a 409.
	 */
	async archiveRun(
		extendedSearchRunId: number,
		requestConfig = {},
	): Promise<AxiosResponse<TExtendedSearchRunDetail>> {
		return this.doPost<TExtendedSearchRunDetail, Record<string, never>>(
			`/extended-search/runs/${extendedSearchRunId}/archive`,
			undefined,
			requestConfig,
		);
	}

	/** Brings an archived run back exactly as it was: a Ready run can be sent to import again. */
	async restoreRun(
		extendedSearchRunId: number,
		requestConfig = {},
	): Promise<AxiosResponse<TExtendedSearchRunDetail>> {
		return this.doPost<TExtendedSearchRunDetail, Record<string, never>>(
			`/extended-search/runs/${extendedSearchRunId}/restore`,
			undefined,
			requestConfig,
		);
	}

	/** The company target list of a project, feeding the run form's "load from project" button. */
	async getProjectCompanies(
		projectId: number,
		requestConfig = {},
	): Promise<AxiosResponse<TExtendedSearchProjectCompaniesResponse>> {
		return this.doGet<TExtendedSearchProjectCompaniesResponse>(
			`/extended-search/projects/${projectId}/company-urls`,
			requestConfig,
		);
	}

	async getLimits(requestConfig = {}): Promise<AxiosResponse<TExtendedSearchLimits>> {
		return this.doGet<TExtendedSearchLimits>("/extended-search/limits", requestConfig);
	}

	/** The import machinery right now: landing pace, queues, MiniMax load and backlog. */
	async getPipelineStatus(
		requestConfig = {},
	): Promise<AxiosResponse<TExtendedSearchPipelineStatus>> {
		return this.doGet<TExtendedSearchPipelineStatus>(
			"/extended-search/pipeline-status",
			requestConfig,
		);
	}

	/** The import queue: whether it is paused, and its runs in the order they will start. */
	async getImportQueue(requestConfig = {}): Promise<AxiosResponse<TExtendedSearchImportQueue>> {
		return this.doGet<TExtendedSearchImportQueue>(
			"/extended-search/import-queue",
			requestConfig,
		);
	}

	/**
	 * Puts a run at the end of the queue, or gives a queued run a new project. The project is
	 * always sent, null included, for the same reason startImport always sends it: the run is
	 * imported later with nobody there to say where its people go.
	 */
	async queueRun(
		extendedSearchRunId: number,
		body: TExtendedSearchQueueRunRequest,
		requestConfig = {},
	): Promise<AxiosResponse<TExtendedSearchImportQueue>> {
		return this.doPut<TExtendedSearchImportQueue, TExtendedSearchQueueRunRequest>(
			`/extended-search/runs/${extendedSearchRunId}/queue`,
			body,
			requestConfig,
		);
	}

	async dequeueRun(
		extendedSearchRunId: number,
		requestConfig = {},
	): Promise<AxiosResponse<TExtendedSearchImportQueue>> {
		return this.doDelete<TExtendedSearchImportQueue, Record<string, never>>(
			`/extended-search/runs/${extendedSearchRunId}/queue`,
			undefined,
			requestConfig,
		);
	}

	/** Every run in the queue, first to start first. A 409 means the queue changed meanwhile. */
	async reorderQueue(
		body: TExtendedSearchQueueOrderRequest,
		requestConfig = {},
	): Promise<AxiosResponse<TExtendedSearchImportQueue>> {
		return this.doPut<TExtendedSearchImportQueue, TExtendedSearchQueueOrderRequest>(
			"/extended-search/import-queue/order",
			body,
			requestConfig,
		);
	}

	async setQueuePaused(
		body: TExtendedSearchQueuePauseRequest,
		requestConfig = {},
	): Promise<AxiosResponse<TExtendedSearchImportQueue>> {
		return this.doPut<TExtendedSearchImportQueue, TExtendedSearchQueuePauseRequest>(
			"/extended-search/import-queue/pause",
			body,
			requestConfig,
		);
	}
}

const mapXExtendedSearchAPI = new ExtendedSearchApi();

export default mapXExtendedSearchAPI;
